import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import usePrevious from '@lib/hooks/usePrevious';

const useElementDimensions = (
  ref: RefObject<HTMLElement | null>,
  key?: string,
) => {
  const [dimensions, setDimensions] = useState<DOMRect | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const prevKey = usePrevious(key);

  const refresh = useCallback(() => {
    const node = ref.current;
    if (node) {
      setDimensions(node.getBoundingClientRect());
    }
  }, [ref]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return () => undefined;

    observerRef.current = new ResizeObserver(() => {
      refresh();
    });

    observerRef.current.observe(node);

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [ref, refresh]);

  useEffect(() => {
    if (prevKey !== key) {
      requestAnimationFrame(() => refresh());
    }
  }, [prevKey, key, refresh]);

  return { dimensions, refresh };
};

export default useElementDimensions;
